import { Anchor, Box, Group, SimpleGrid, Stack, Text, UnstyledButton } from "@mantine/core"
import type { MouseEvent } from "react"

import { CivalaMark } from "../CivalaMark"

// Canonical Civala marketing footer — dark navy (--pitch-page-dark) full-bleed.
//
// Router-agnostic mirror of TopNav. Pass navItems with onClick to integrate
// with a router; otherwise plain href anchors apply.

export type FooterNavItem = {
  id: string
  label: string
  href: string
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void
}

export type FooterProps = {
  navItems: ReadonlyArray<FooterNavItem>
  /** Tagline beneath the brand mark. */
  tagline?: string
  /** Contact email shown beneath the tagline (rendered as mailto). */
  contactEmail?: string
  /** Legal column items. */
  legalItems?: ReadonlyArray<string>
  /** Related column items. */
  relatedItems?: ReadonlyArray<string>
  /** Bottom-bar left text (e.g., legal entity + copyright). */
  bottomLeft?: string
  /** Bottom-bar right text (e.g., version / build tag). */
  bottomRight?: string
}

const DEFAULT_TAGLINE = "A federation of biotech companies purpose-built for institutional capital."
const DEFAULT_LEGAL: ReadonlyArray<string> = [
  "Privacy Policy",
  "Terms of Use",
  "Confidentiality Notice",
  "LP Disclosure",
]
const DEFAULT_RELATED: ReadonlyArray<string> = [
  "Civala Investor Deck v5.0",
  "Governance Architecture",
  "Civic Innovation Trust",
]
const DEFAULT_BOTTOM_LEFT = "Civala Bio Syndicate I LLC · © 2026 · Confidential — For Accredited Investors Only"
const DEFAULT_BOTTOM_RIGHT = "civala.ai · v1.0 · April 2026"

export function Footer({
  navItems,
  tagline = DEFAULT_TAGLINE,
  contactEmail = "lp@civala.com",
  legalItems = DEFAULT_LEGAL,
  relatedItems = DEFAULT_RELATED,
  bottomLeft = DEFAULT_BOTTOM_LEFT,
  bottomRight = DEFAULT_BOTTOM_RIGHT,
}: FooterProps) {
  return (
    <Box component="footer" py={48} bg="var(--pitch-page-dark)">
      <Box maw={1280} mx="auto" px="lg">
        <SimpleGrid cols={{ base: 1, md: 4 }} spacing="xl" mb={32}>
          <Stack gap="sm">
            <Group gap={10} align="baseline" wrap="nowrap">
              <CivalaMark size={22} ringColor="white" />
              <Text ff="'Playfair Display', Georgia, serif" fz={28} c="white">
                civala
              </Text>
            </Group>
            <Text fz={13} lh={1.5} c="var(--pitch-text-tertiary)">
              {tagline}
            </Text>
            <Anchor href={`mailto:${contactEmail}`} fz={13} c="var(--pitch-text-tertiary)" underline="hover">
              {contactEmail}
            </Anchor>
          </Stack>

          <Stack gap="sm">
            <Text fz={11} lts="0.08em" tt="uppercase" c="white" opacity={0.6}>
              Site
            </Text>
            {navItems.map((n) => (
              <UnstyledButton
                key={n.id}
                component="a"
                href={n.href}
                onClick={(e) => {
                  if (n.onClick) {
                    e.preventDefault()
                    n.onClick(e)
                  }
                }}
                fz={13}
                c="var(--pitch-text-tertiary)"
                style={{ textAlign: "left" }}
              >
                {n.label}
              </UnstyledButton>
            ))}
          </Stack>

          <Stack gap="sm">
            <Text fz={11} lts="0.08em" tt="uppercase" c="white" opacity={0.6}>
              Legal
            </Text>
            {legalItems.map((t) => (
              <Text key={t} fz={13} c="var(--pitch-text-tertiary)">
                {t}
              </Text>
            ))}
          </Stack>

          <Stack gap="sm">
            <Text fz={11} lts="0.08em" tt="uppercase" c="white" opacity={0.6}>
              Related
            </Text>
            {relatedItems.map((t) => (
              <Text key={t} fz={13} c="var(--pitch-text-tertiary)">
                {t}
              </Text>
            ))}
          </Stack>
        </SimpleGrid>

        <Box
          pt="md"
          style={{
            borderTop: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Text fz={11} c="var(--pitch-text-tertiary)">
            {bottomLeft}
          </Text>
          <Text fz={11} c="var(--pitch-text-tertiary)">
            {bottomRight}
          </Text>
        </Box>
      </Box>
    </Box>
  )
}
